import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext, useSearchParams, Navigate } from 'react-router-dom';
import MyAccountContentHeader from '../MyAccountContentHeader/MyAccountContentHeader';
import MyAccountSubscriptionOrderCard from '../MyAccountSubscriptionOrderCard/MyAccountSubscriptionOrderCard';
import * as translator from '../../../../utils/translator';
import SubscriptionCategoriesSuggestions from './SubscriptionCategoriesSuggestions';
import SubscriptionRootCategoryCard from './SubscriptionRootCategoryCard';
import CarePlusPlanCard from '../CarePlus/CarePlusPlanCard/CarePlusPlanCard';
import AppInstallBanner from '../../../../components/AppInstallBanner/AppInstallBanner';
import * as http from '../../../../utils/http';
import mensHealth from '../../../../images/MyAccountSubscriptions/mens_health.png';
import womensHealth from '../../../../images/MyAccountSubscriptions/womens_health.jpg';
import generalMedicine from '../../../../images/MyAccountSubscriptions/general_medicine.png';

interface MyAccountSubscriptionsProps {
	showOneCardWithEditPanel: boolean;
}

type MySubscriptions = {
	subscriptionData: any;
	subscriptionCount: number;
	subscriptionCategoriesSuggestions: Record<
		string,
		Array<{
			root_title: string;
			id: number;
			title: string;
			mention: string;
			shortcut: string;
      link: string;
		}>
	>;
};

const MyAccountSubscriptions: React.FC<MyAccountSubscriptionsProps> = ({ showOneCardWithEditPanel }) => {
	const { setActiveMenuLinkId, country, language, setStickyNotificationDetails, setLoading, setError, refreshData, asideBarData } = useOutletContext<any>();
	const [searchParams] = useSearchParams();
	const { order_hash_id: hashID } = useParams<{ order_hash_id: string | undefined }>(); //the hashID from url
	const showCancelModal = searchParams.get('showCancelModal') === 'true';

	const [mySubscriptions, setMySubscriptions] = useState<MySubscriptions>({
		subscriptionData: {},
		subscriptionCount: 0,
		subscriptionCategoriesSuggestions: {},
	});
	const [hasLoaded, setHasLoaded] = useState(false);
	/**
	 * A Care+ plan (a plan with a tier) is managed on its order page (pause, product change, cancellation)
	 * and shown here by the Care+ plan card. It never gets the regular subscription card, whose edit panel
	 * offers the regular skip / change medication / cancel that know nothing of Care+.
	 */
	const isCarePlusSubscription = (card: any) => Boolean(card?.isCarePlus);
	const subscriptionOrders = Object.values(mySubscriptions.subscriptionData).filter((card) => !isCarePlusSubscription(card));
	const focusedCard = showOneCardWithEditPanel && hashID ? mySubscriptions.subscriptionData[hashID] : undefined;
	const categoriesSuggestions = mySubscriptions.subscriptionCategoriesSuggestions;

	// A Care Plus plan carries no subscriptionData rows, so the count alone would miss it.
	const hasPlan = subscriptionOrders.length > 0 || Boolean(asideBarData?.carePlusSubscriptionHashId);

	// Map root_code to specific image
	const getImageByRootCodeCategory = (rootCodeCategory: string) => {
		if (rootCodeCategory.includes('women')) return womensHealth;
		if (rootCodeCategory.includes('men')) return mensHealth;
		return generalMedicine;
	};

	let description = translator.translate(language, 'my_account', 'SubscriptionOrders/subTitle');
	if (!mySubscriptions.subscriptionCount && !asideBarData?.hasActiveCarePlusSubscription && !asideBarData?.carePlusSubscriptionHashId) {
		description = translator.translate(language, 'my_account', 'SubscriptionOrders/subTitleNoSubscriptions');
	}

	useEffect(() => {
		setActiveMenuLinkId('orders-subscription');

		setLoading(true);

		http
			.getFromServer(`${country}/account/subscriptions`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
			.then((response) => {
				setMySubscriptions(response.data);
				setHasLoaded(true);
			})
			.catch((error) => {
				window.clientLogger.error('Error in MyAccountSubscriptions: ', error);
				setError(500);
			})
			.finally(() => {
				setLoading(false);
			});

		const errorCodeParam = searchParams.get('ec');
		if (errorCodeParam && errorCodeParam === 'as') {
			//as = already_subscribed
			setStickyNotificationDetails(() => ({
				show: true,
				title: translator.translate(language, 'default', 'StickyNotification/danger_title'),
				content: translator.translate(language, 'default', 'StickyNotification/already_subscribed'),
				level: 'error',
				showInfoIcon: true,
			}));
		}
	}, [showOneCardWithEditPanel, refreshData]);

	if (hashID && !mySubscriptions.subscriptionData[hashID]) {
		return <></>;
	}

	return (
		<>
			<div className="account-content min-w-0">
				<MyAccountContentHeader title={translator.translate(language, 'my_account', 'SubscriptionOrders/title')} description={description} />

				{/* Only with a plan, since the copy is about moving the next delivery; not on the focused edit view */}
				{hasPlan && hasLoaded && !showOneCardWithEditPanel && <AppInstallBanner
					desktopPlacementId="W8"
					mobilePlacementId="W8"
					language={language}
					country={country}
					customerUuid={asideBarData?.uuid}
					isPairedToMobileApp={asideBarData?.isPairedToMobileApp}
					allowedPlacements={asideBarData?.appInstallPlacements}
					className="account-app-install-banner"
				/>}

				{focusedCard && isCarePlusSubscription(focusedCard) ? (
					// the edit view of a Care+ plan is its order page
					<Navigate to={`/${country}/account/my-orders/${hashID}/details`} replace />
				) : showOneCardWithEditPanel && hashID && !mySubscriptions?.subscriptionData?.[hashID] ? (
					//showOneCardWithEditPanel means we Show only 1 spesific subscription by order_hash_id
					<MyAccountSubscriptionOrderCard
						showEditPanel={showOneCardWithEditPanel}
						language={language}
						orderHashId={hashID}
						subscriptionPrice={mySubscriptions.subscriptionData[hashID].subscriptionPrice}
						regularPrice={mySubscriptions.subscriptionData[hashID].regularPrice}
						currencySign={mySubscriptions.subscriptionData[hashID].currencySign}
						orderPeriod={mySubscriptions.subscriptionData[hashID].plan}
						deliveryAddress={mySubscriptions.subscriptionData[hashID].address}
						nextBillingDate={mySubscriptions.subscriptionData[hashID].next_billing}
						nextDeliveryDate={mySubscriptions.subscriptionData[hashID].next_delivery}
						image={mySubscriptions.subscriptionData[hashID].image}
						title={mySubscriptions.subscriptionData[hashID].title}
            orderSubscriptionData={mySubscriptions.subscriptionData[hashID]}
						showCancelModal={showCancelModal}
					/>
				) : asideBarData?.carePlusSubscriptionHashId ? (
					<div className="flex flex-col items-start gap-4 new-subscription-desktop">
						<CarePlusPlanCard subscriptionHashID={asideBarData.carePlusSubscriptionHashId} />
						{subscriptionOrders
							.filter((subscriptionOrder: any) => subscriptionOrder.catalog_mention !== 'weight_loss')
							.map((subscriptionOrder: any, index: number) => (
								<MyAccountSubscriptionOrderCard
									showEditPanel={showOneCardWithEditPanel}
									orderSubscriptionData={subscriptionOrder}
									key={index}
									compact
									language={language}
									orderHashId={subscriptionOrder.hashID}
									subscriptionPrice={subscriptionOrder.subscriptionPrice}
									regularPrice={subscriptionOrder.regularPrice}
									currencySign={subscriptionOrder.currencySign}
									orderPeriod={subscriptionOrder.plan}
									deliveryAddress={subscriptionOrder.address}
									nextBillingDate={subscriptionOrder.next_billing}
									nextDeliveryDate={subscriptionOrder.next_delivery}
									image={subscriptionOrder.image}
									title={subscriptionOrder.title}
								/>
							))}
						{Object.entries(categoriesSuggestions).map(([rootCodeCategory, categories]: [string, any[]]) => (
							<SubscriptionRootCategoryCard key={rootCodeCategory} rootCodeCategory={rootCodeCategory} rootTitle={categories[0].root_title} categories={categories} image={getImageByRootCodeCategory(rootCodeCategory)} />
						))}
					</div>
				) : !subscriptionOrders.length ? (
					<SubscriptionCategoriesSuggestions categoriesSuggestions={categoriesSuggestions} />
				) : (
					subscriptionOrders.map((subscriptionOrder: any, index: number) => (
						<MyAccountSubscriptionOrderCard
							showEditPanel={showOneCardWithEditPanel}
              orderSubscriptionData={subscriptionOrder}
							key={index}
							language={language}
							orderHashId={subscriptionOrder.hashID}
							subscriptionPrice={subscriptionOrder.subscriptionPrice}
							regularPrice={subscriptionOrder.regularPrice}
							currencySign={subscriptionOrder.currencySign}
							orderPeriod={subscriptionOrder.plan}
							deliveryAddress={subscriptionOrder.address}
							nextBillingDate={subscriptionOrder.next_billing}
							nextDeliveryDate={subscriptionOrder.next_delivery}
							image={subscriptionOrder.image}
							title={subscriptionOrder.title}
						/>
					))
				)}
			</div>
		</>
	);
};

export default MyAccountSubscriptions;
